// src/context/CartContext.js
import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { cartAPI } from '@/api/cart.api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const CartContext = createContext(null);

const GUEST_CART_KEY = 'guest_cart';
const GST_RATE = 0.18; // retained for reference only — checkout uses backend summary


export function CartProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Prevents double-merge on re-renders after login
  const mergeAttemptedRef = useRef(false);
  
  // ============================================
  // FETCH CART (FROM BACKEND OR LOCALSTORAGE)
  // ============================================
  
  const fetchCart = useCallback(async () => {
    if (isAuthenticated) {
      // Logged-in user - fetch from backend
      setLoading(true);
      try {
        const cartData = await cartAPI.getCart();
        setItems(cartData.items || []);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch cart:', err);
        setError('Failed to load cart');
        
        // Fallback to localStorage if backend fails
        const localCart = getLocalCart();
        setItems(localCart);
      } finally {
        setLoading(false);
      }
    } else {
      // Guest user - load from localStorage
      const localCart = getLocalCart();
      setItems(localCart);
    }
  }, [isAuthenticated]);

  // ============================================
  // LOAD CART ON MOUNT AND AUTH CHANGE
  // ============================================
  
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // ============================================
  // MERGE GUEST CART ON LOGIN
  // ============================================
  
  useEffect(() => {
    const mergeGuestCart = async () => {
      if (!isAuthenticated || !user) {
        mergeAttemptedRef.current = false; // reset on logout so next login merges again
        return;
      }
      if (mergeAttemptedRef.current) return; // already ran for this login session
      mergeAttemptedRef.current = true;

      const guestCart = getLocalCart();
      if (guestCart.length > 0) {
        try {
          await cartAPI.mergeGuestCart(guestCart);
          clearLocalCart();
          await fetchCart();
          toast.success(`${guestCart.length} items merged into your cart`);
        } catch (err) {
          console.error('Failed to merge guest cart:', err);
          toast.error('Some items could not be added to your cart');
        }
      }
    };
    mergeGuestCart();
  // fetchCart intentionally omitted — its reference changes with isAuthenticated,
  // which would re-trigger the merge. isAuthenticated + user are sufficient.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  // ============================================
  // SYNC TO LOCALSTORAGE (GUEST ONLY)
  // ============================================

  useEffect(() => {
    // BUG-FIX: Guard on BOTH isAuthenticated AND user being absent.
    // Previously, guarding only on !isAuthenticated meant that during an auth
    // token refresh (where isAuthenticated briefly flips false while `user`
    // still exists), the server cart items were written to localStorage.
    // On re-authentication, mergeGuestCart() would then merge those items back
    // into the server cart, doubling quantities.
    if (!isAuthenticated && !user) {
      saveLocalCart(items);
    }
  }, [items, isAuthenticated, user]);

  // ============================================
  // ADD ITEM
  // ============================================
  
  const addItem = useCallback(async (product, quantity = 1) => {
    setError(null);
  
    // ===============================
    // VALIDATION
    // ===============================
    if (!product || !product.id || !product.name || product.price == null) {
      const message = "Invalid product data";
      setError(message);
      toast.error(message);
      return;
    }
  
    if (!Number.isInteger(quantity) || quantity <= 0) {
      const message = "Invalid quantity";
      setError(message);
      toast.error(message);
      return;
    }
  
    try {
      setLoading(true);
  
      // ===============================
      // AUTHENTICATED USER (SERVER CART)
      // ===============================
      if (isAuthenticated) {
        const updatedCart = await cartAPI.addItem({
          product_id: product.id,
          quantity,
        });
  
        // Safety check
        if (!updatedCart?.items) {
          throw new Error("Invalid cart response from server");
        }
  
        setItems(updatedCart.items);
      } 
      // ===============================
      // GUEST USER (LOCAL CART)
      // ===============================
      else {
        setItems((prevItems) => {
          const existingIndex = prevItems.findIndex(
            (item) => item.id === product.id
          );
  
          if (existingIndex !== -1) {
            // Increase quantity
            return prevItems.map((item, index) =>
              index === existingIndex
                ? { ...item, quantity: item.quantity + quantity }
                : item
            );
          }
  
          // Add new item
          return [
            ...prevItems,
            {
              id: product.id,
              name: product.name,
              price: product.price,
              image: product.image || null,
              quantity,
            },
          ];
        });
      }
  
      toast.success(`${product.name} added to cart`);
    } catch (err) {
      console.error("Failed to add item:", err);
  
      const message =
        err.response?.data?.detail ||
        err.message ||
        "Failed to add item";
  
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);  // BUG-01
  
  // ============================================
// UPDATE QUANTITY
// ============================================

const updateQuantity = useCallback(async (itemId, newQuantity) => {
  if (newQuantity <= 0) {
    return removeItem(itemId);
  }

  setLoading(true);
  setError(null);

  try {
    if (isAuthenticated) {
      const updatedCart = await cartAPI.updateQuantity(itemId, newQuantity);
      setItems(updatedCart.items);
    } else {
      setItems((prev) =>
        prev.map((item) =>
          // ✅ FIX: Check both product_id and id
          (item.product_id === itemId || item.id === itemId)
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
    }
    
    toast.success('Quantity updated');
  } catch (err) {
    console.error('Failed to update quantity:', err);
    const message = err.response?.data?.detail || 'Failed to update quantity';
    setError(message);
    toast.error(message);
  } finally {
    setLoading(false);
  }
}, [isAuthenticated]);  // BUG-01

// ============================================
// INCREMENT/DECREMENT QUANTITY
// ============================================

const incrementQuantity = useCallback(async (itemId) => {
  // ✅ FIX: Find by product_id OR id
  const item = items.find((i) => i.product_id === itemId || i.id === itemId);
  if (item) {
    await updateQuantity(itemId, item.quantity + 1);
  }
}, [items, updateQuantity]);  // BUG-01

const decrementQuantity = useCallback(async (itemId) => {
  // ✅ FIX: Find by product_id OR id
  const item = items.find((i) => i.product_id === itemId || i.id === itemId);
  if (item) {
    await updateQuantity(itemId, Math.max(0, item.quantity - 1));
  }
}, [items, updateQuantity]);  // BUG-01

  // ============================================
// REMOVE ITEM
// ============================================

const removeItem = useCallback(async (itemId) => {
  setLoading(true);
  setError(null);

  // Optimistic update
  const previousItems = [...items];
  
  // ✅ FIX: Check both product_id AND id for compatibility
  setItems((prev) => prev.filter((item) => 
    item.product_id !== itemId && item.id !== itemId
  ));

  try {
    if (isAuthenticated) {
      await cartAPI.removeItem(itemId);
    }
    
    toast.success('Item removed from cart');
  } catch (err) {
    console.error('Failed to remove item:', err);
    
    // Rollback on error
    setItems(previousItems);
    
    const message = err.response?.data?.detail || 'Failed to remove item';
    setError(message);
    toast.error(message);
  } finally {
    setLoading(false);
  }
  }, [isAuthenticated]);  // BUG-01: useCallback so value object stays stable
  
  
  // ============================================
  // CLEAR CART
  // ============================================
  
  const clearCart = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (isAuthenticated) {
        await cartAPI.clearCart();
      }
      
      setItems([]);
      clearLocalCart();
      
      toast.success('Cart cleared');
    } catch (err) {
      console.error('Failed to clear cart:', err);
      const message = err.response?.data?.detail || 'Failed to clear cart';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);  // BUG-01

  // ============================================
  // COMPUTED VALUES
  // ============================================
  
  // ============================================
// COMPUTED VALUES
// ============================================

const itemCount = items.reduce(
  (sum, item) => sum + item.quantity,
  0
);

const subtotal = items.reduce(
  (sum, item) => sum + Number(item.price) * item.quantity,
  0
);
// GST and total are derived server-side in the checkout summary.
// Do not compute them here to avoid showing a different number than checkout.

  // ============================================
  // CONTEXT VALUE
  // ============================================
  
  // BUG-01 fix: useMemo prevents a new object reference on every render,
  // which would cause all useCart() consumers to re-render even when the
  // specific field they read has not changed.
  const value = useMemo(() => ({
    items,
    itemCount,
    subtotal,
    loading,
    error,
    addItem,
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    removeItem,
    clearCart,
    refreshCart: fetchCart,
  }), [
    items, loading, error,
    addItem, updateQuantity, incrementQuantity,
    decrementQuantity, removeItem, clearCart, fetchCart,
  ]);
  

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

// ============================================
// LOCALSTORAGE HELPERS
// ============================================

function getLocalCart() {
  try {
    const stored = localStorage.getItem(GUEST_CART_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (err) {
    console.error('Failed to parse cart from localStorage:', err);
    return [];
  }
}

function saveLocalCart(items) {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
  } catch (err) {
    console.error('Failed to save cart to localStorage:', err);
  }
}

function clearLocalCart() {
  try {
    localStorage.removeItem(GUEST_CART_KEY);
  } catch (err) {
    console.error('Failed to clear cart from localStorage:', err);
  }
}

// ============================================
// HOOK
// ============================================

export function useCart() {
  const context = useContext(CartContext);

  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
}